import { START_LOCATION } from 'vue-router';

import router from '@/core/router';

// Single-flight: while a query keeps failing with a dead session, the error
// link fires on every retry before the redirect lands. Without the latch each
// call queues another navigation, which re-runs the auth guard, which fetches
// `me`, which fails again — a loop.
let loginRedirectInFlight = false;

/** Send the user to sign in, coming back to where they are now. */
export const goToLogin = (): void => {
  // Before the first navigation lands there is no "here" to come back to, and
  // a redirect would cancel that navigation; the guard owns that moment.
  if (loginRedirectInFlight || router.currentRoute.value === START_LOCATION || router.currentRoute.value.name === 'Login') {
    return;
  }

  loginRedirectInFlight = true;

  void router.replace({
    name: 'Login',
    query: { redirect_uri: router.currentRoute.value.fullPath },
  }).finally(() => {
    loginRedirectInFlight = false;
  });
};

/** The API's REST sign-out endpoint (clears the HttpOnly session cookies). */
export const getLogoutUrl = (): string => `${import.meta.env.VITE_OMB_API_URL}/auth/logout`;

/**
 * An in-app path to return to after sign-in, or `null`. Only a single string
 * (not a repeated `?redirect_uri=`, which arrives as an array) that resolves to
 * this origin counts — parsed as the browser would, so `//host`, `/\\host` and
 * `/<tab>/host` are refused even if a later caller hands the result to
 * `window.location` instead of the router.
 */
export const safeRedirectPath = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return null;
  }

  try {
    const url = new URL(value, window.location.origin);

    return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch {
    return null;
  }
};

/*
 * The magic-link round trip leaves through the email and comes back on
 * /magic-link, without the `?redirect_uri=` the sign-in form had. The target
 * is parked in a short-lived cookie on submit and read back after sign-in.
 * Its lifetime covers the longest path: the 7-day welcome link plus the
 * 10-minute 2FA window, with a little slack for clock skew.
 */
const REDIRECT_COOKIE = 'omb_app_auth_redirect';
const REDIRECT_COOKIE_MAX_AGE_S = 7 * 24 * 60 * 60 + 15 * 60;

const secureFlag = (): string => (window.location.protocol === 'https:' ? '; Secure' : '');

/** Park the post-sign-in target; anything but a safe path clears it. */
export const stashAuthRedirect = (value: unknown): void => {
  const path = safeRedirectPath(value);

  document.cookie = path
    ? `${REDIRECT_COOKIE}=${encodeURIComponent(path)}; path=/; max-age=${REDIRECT_COOKIE_MAX_AGE_S}; SameSite=Lax${secureFlag()}`
    : `${REDIRECT_COOKIE}=; path=/; max-age=0; SameSite=Lax${secureFlag()}`;
};

/** Read the parked target (if still safe) without clearing it. */
export const readAuthRedirect = (): string | null => {
  const raw = new RegExp(`(?:^|;\\s*)${REDIRECT_COOKIE}=([^;]+)`).exec(document.cookie)?.[1];

  if (!raw) {
    return null;
  }

  try {
    return safeRedirectPath(decodeURIComponent(raw));
  } catch {
    return null;
  }
};

/** Read and clear the parked target — single use, so a later sign-in can't inherit it. */
export const consumeAuthRedirect = (): string => {
  const target = readAuthRedirect() ?? '/';

  stashAuthRedirect(null);

  return target;
};
